import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, AlertCircle, MapPin, Eye, Users, Globe, CreditCard, ChevronDown, ChevronRight
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebaseService';
import { NORDIC_PRESCAN_GROUPS, getPrescanCountryForRegion } from '../../../utils/prescanRegions';

interface UserData {
  homeCounty?: string;
  subscriptionStatus?: string;
  platform?: string;
  isTester?: boolean;
  subscriptionSandbox?: boolean;
}

interface RegionStats {
  homeRegions: Record<string, number>;
  homeRegionsPaid: Record<string, number>;
  regionViews: Record<string, number>;
  regionViewsLast7: Record<string, number>;
  regionViewsLast30: Record<string, number>;
  totalUsers: number;
  paidUsers: number;
  usersWithHome: number;
  paidWithHome: number;
}

const EMPTY_REGION_STATS: RegionStats = {
  homeRegions: {},
  homeRegionsPaid: {},
  regionViews: {},
  regionViewsLast7: {},
  regionViewsLast30: {},
  totalUsers: 0,
  paidUsers: 0,
  usersWithHome: 0,
  paidWithHome: 0,
};

const AdminRegionDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<RegionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewPeriod, setViewPeriod] = useState<'all' | '30d' | '7d'>('30d');
  const [showPaidOnly, setShowPaidOnly] = useState(false);
  const [openCountries, setOpenCountries] = useState<Record<string, boolean>>({ Norge: true });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersSnap, viewsSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'region_views')),
      ]);

      const homeRegions: Record<string, number> = {};
      const homeRegionsPaid: Record<string, number> = {};
      let totalUsers = 0;
      let paidUsers = 0;
      let usersWithHome = 0;
      let paidWithHome = 0;

      usersSnap.docs.forEach(doc => {
        totalUsers++;
        const data = doc.data() as UserData;
        const isPaid = data.subscriptionStatus === 'paid' && !data.isTester && !data.subscriptionSandbox;
        if (isPaid) paidUsers++;
        if (data.homeCounty) {
          usersWithHome++;
          homeRegions[data.homeCounty] = (homeRegions[data.homeCounty] || 0) + 1;
          if (isPaid) {
            paidWithHome++;
            homeRegionsPaid[data.homeCounty] = (homeRegionsPaid[data.homeCounty] || 0) + 1;
          }
        }
      });

      const regionViews: Record<string, number> = {};
      const regionViewsLast7: Record<string, number> = {};
      const regionViewsLast30: Record<string, number> = {};

      const d7 = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      const d30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

      viewsSnap.docs.forEach(docSnap => {
        const date = docSnap.id;
        const data = docSnap.data();
        for (const [county, count] of Object.entries(data)) {
          if (typeof count !== 'number') continue;
          regionViews[county] = (regionViews[county] || 0) + count;
          if (date >= d7) regionViewsLast7[county] = (regionViewsLast7[county] || 0) + count;
          if (date >= d30) regionViewsLast30[county] = (regionViewsLast30[county] || 0) + count;
        }
      });

      setStats({ homeRegions, homeRegionsPaid, regionViews, regionViewsLast7, regionViewsLast30, totalUsers, paidUsers, usersWithHome, paidWithHome });
    } catch (e: any) {
      setError(e.message || 'Kunne ikke hente regiondata');
    } finally {
      setLoading(false);
    }
  };

  const safeStats = stats || EMPTY_REGION_STATS;

  const coveragePct = safeStats.totalUsers > 0
    ? Math.round((safeStats.usersWithHome / safeStats.totalUsers) * 100)
    : 0;

  const activeHomeRegions = showPaidOnly ? safeStats.homeRegionsPaid : safeStats.homeRegions;
  const sortedHome = Object.entries(activeHomeRegions).sort((a, b) => b[1] - a[1]);
  const maxHome = sortedHome[0]?.[1] || 1;

  const activeViews = viewPeriod === '7d'
    ? safeStats.regionViewsLast7
    : viewPeriod === '30d'
      ? safeStats.regionViewsLast30
      : safeStats.regionViews;
  const sortedViews = Object.entries(activeViews).sort((a, b) => b[1] - a[1]);
  const maxViews = sortedViews[0]?.[1] || 1;
  const totalViews = sortedViews.reduce((s, [, c]) => s + c, 0);

  const groupedHome = useMemo(() => {
    const byCountry: Record<string, Array<[string, number]>> = {};
    sortedHome.forEach(([county, count]) => {
      const country = getPrescanCountryForRegion(county);
      if (!byCountry[country]) byCountry[country] = [];
      byCountry[country].push([county, count]);
    });
    return byCountry;
  }, [sortedHome]);

  const groupedViews = useMemo(() => {
    const byCountry: Record<string, Array<[string, number]>> = {};
    sortedViews.forEach(([county, count]) => {
      const country = getPrescanCountryForRegion(county);
      if (!byCountry[country]) byCountry[country] = [];
      byCountry[country].push([county, count]);
    });
    return byCountry;
  }, [sortedViews]);

  // Combined table: all counties from both datasets
  const allCounties = new Set([...Object.keys(activeHomeRegions), ...Object.keys(activeViews)]);
  const combinedRows = Array.from(allCounties)
    .map(county => ({
      country: getPrescanCountryForRegion(county),
      county,
      users: activeHomeRegions[county] || 0,
      views: activeViews[county] || 0,
    }))
    .sort((a, b) => b.views - a.views || b.users - a.users);

  const groupedCombinedRows = useMemo(() => {
    const byCountry: Record<string, typeof combinedRows> = {};
    combinedRows.forEach(row => {
      if (!byCountry[row.country]) byCountry[row.country] = [];
      byCountry[row.country].push(row);
    });
    return byCountry;
  }, [combinedRows]);

  const countrySections = useMemo(() => {
    const known = NORDIC_PRESCAN_GROUPS.map(group => group.country);
    return [...known, 'Andre'].filter(country =>
      groupedHome[country]?.length || groupedViews[country]?.length || groupedCombinedRows[country]?.length
    );
  }, [groupedCombinedRows, groupedHome, groupedViews]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster regiondata...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-4">
        <AlertCircle className="text-red-400 shrink-0" size={18} />
        <div>
          <p className="text-sm font-bold text-red-400">Regiondata utilgjengelig</p>
          <p className="text-xs text-red-400/70 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">

      {/* Paid filter toggle */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => setShowPaidOnly(prev => !prev)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${showPaidOnly ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' : 'bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:border-amber-500/40'}`}
        >
          <CreditCard size={12} />
          {showPaidOnly ? `Betalende (${stats.paidUsers})` : 'Vis kun betalende'}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`border p-5 rounded-3xl ${showPaidOnly ? 'bg-amber-500/5 border-amber-500/20' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${showPaidOnly ? 'bg-amber-500/10' : 'bg-violet-500/10'}`}>
              {showPaidOnly ? <CreditCard size={16} className="text-amber-400" /> : <Users size={16} className="text-violet-400" />}
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{showPaidOnly ? 'Betalende' : 'Totalt brukere'}</p>
          </div>
          <p className={`text-3xl font-black ${showPaidOnly ? 'text-amber-400' : 'text-[var(--text-primary)]'}`}>{(showPaidOnly ? stats.paidUsers : stats.totalUsers).toLocaleString('nb-NO')}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">{showPaidOnly ? 'betalende brukere' : 'registrerte brukere'}</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <MapPin size={16} className="text-emerald-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Med hjemregion</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{(showPaidOnly ? stats.paidWithHome : stats.usersWithHome).toLocaleString('nb-NO')}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">{showPaidOnly ? `${stats.paidUsers > 0 ? Math.round((stats.paidWithHome / stats.paidUsers) * 100) : 0}% dekning` : `${coveragePct}% dekning`}</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <Eye size={16} className="text-indigo-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Kartvisninger</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{totalViews.toLocaleString('nb-NO')}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">
            {viewPeriod === '7d' ? 'siste 7 dager' : viewPeriod === '30d' ? 'siste 30 dager' : 'alle tider'}
            {showPaidOnly ? ' (alle brukere*)' : ''}
          </p>
        </div>
      </div>

      {/* Where users live */}
      <div className={`border rounded-panel p-8 ${showPaidOnly ? 'bg-amber-500/5 border-amber-500/15' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
        <div className="flex items-center gap-4 mb-8">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${showPaidOnly ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
            <MapPin size={20} className={showPaidOnly ? 'text-amber-400' : 'text-emerald-400'} />
          </div>
          <div>
            <p className="text-lg font-black text-[var(--text-primary)]">{showPaidOnly ? 'Hvor bor betalende brukere' : 'Hvor bor brukerne'}</p>
            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Hjemfylke basert på GPS</p>
          </div>
        </div>

        {sortedHome.length === 0 ? (
          <p className="text-2xs text-[var(--text-muted)]">{showPaidOnly ? 'Ingen betalende brukere med hjemregion ennå.' : 'Ingen hjemregion-data ennå. Data samles automatisk ved innlogging.'}</p>
        ) : (
          <div className="space-y-4">
            {countrySections.filter(country => groupedHome[country]?.length).map(country => {
              const countryRows = groupedHome[country] || [];
              const countryTotal = countryRows.reduce((sum, [, count]) => sum + count, 0);
              const isOpen = openCountries[country] !== false;
              return (
                <div key={`home-${country}`} className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenCountries(prev => ({ ...prev, [country]: prev[country] === false }))}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[var(--bg-input)] transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}
                      <span className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest truncate">{country}</span>
                    </div>
                    <span className="text-xs font-black text-[var(--text-primary)]">{countryTotal.toLocaleString('nb-NO')}</span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3">
                      {countryRows.map(([county, count]) => {
                        const pct = (count / maxHome) * 100;
                        return (
                          <div key={county} className="flex items-center gap-4">
                            <span className="w-36 text-2xs font-bold text-[var(--text-secondary)] truncate shrink-0">{county}</span>
                            <div className="flex-1 bg-[var(--bg-input)] h-6 rounded-lg overflow-hidden">
                              <div
                                className={`${showPaidOnly ? 'bg-amber-500/30' : 'bg-emerald-500/30'} h-full rounded-lg transition-all duration-700`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-sm font-black text-[var(--text-primary)] w-12 text-right shrink-0">
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPaidOnly && (
        <p className="text-3xs text-[var(--text-muted)] text-center">
          * Kartvisninger kan ikke filtreres per brukertype — viser alle brukere. Hjemregion-data viser kun betalende.
        </p>
      )}

      {/* Where users look */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
              <Eye size={20} className="text-indigo-400" />
            </div>
            <div>
              <p className="text-lg font-black text-[var(--text-primary)]">Hvor ser brukerne</p>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Kartvisninger per fylke</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl p-1">
            {(['7d', '30d', 'all'] as const).map(p => (
              <button
                key={p}
                onClick={() => setViewPeriod(p)}
                className={`px-3 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all ${
                  viewPeriod === p
                    ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                {p === '7d' ? '7d' : p === '30d' ? '30d' : t('common.all')}
              </button>
            ))}
          </div>
        </div>

        {sortedViews.length === 0 ? (
          <p className="text-2xs text-[var(--text-muted)]">Ingen kartvisningsdata for valgt periode.</p>
        ) : (
          <div className="space-y-4">
            {countrySections.filter(country => groupedViews[country]?.length).map(country => {
              const countryRows = groupedViews[country] || [];
              const countryTotal = countryRows.reduce((sum, [, count]) => sum + count, 0);
              const isOpen = openCountries[country] !== false;
              return (
                <div key={`views-${country}`} className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenCountries(prev => ({ ...prev, [country]: prev[country] === false }))}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[var(--bg-input)] transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}
                      <span className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest truncate">{country}</span>
                    </div>
                    <span className="text-xs font-black text-[var(--text-primary)]">{countryTotal.toLocaleString('nb-NO')}</span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3">
                      {countryRows.map(([county, count]) => {
                        const pct = (count / maxViews) * 100;
                        return (
                          <div key={county} className="flex items-center gap-4">
                            <span className="w-36 text-2xs font-bold text-[var(--text-secondary)] truncate shrink-0">{county}</span>
                            <div className="flex-1 bg-[var(--bg-input)] h-6 rounded-lg overflow-hidden">
                              <div
                                className="bg-indigo-500/30 h-full rounded-lg transition-all duration-700"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-sm font-black text-[var(--text-primary)] w-12 text-right shrink-0">
                              {count.toLocaleString('nb-NO')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Combined table */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-violet-500/10 flex items-center justify-center">
            <Globe size={20} className="text-violet-400" />
          </div>
          <div>
            <p className="text-lg font-black text-[var(--text-primary)]">Fylkesoversikt</p>
            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{showPaidOnly ? 'Betalende brukere' : 'Alle brukere'} + kartvisninger</p>
          </div>
        </div>

        <div className="space-y-4">
          {countrySections.filter(country => groupedCombinedRows[country]?.length).map(country => {
            const countryRows = groupedCombinedRows[country] || [];
            const isOpen = openCountries[country] !== false;
            const countryUsers = countryRows.reduce((sum, row) => sum + row.users, 0);
            const countryViews = countryRows.reduce((sum, row) => sum + row.views, 0);

            return (
              <div key={`combined-${country}`} className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenCountries(prev => ({ ...prev, [country]: prev[country] === false }))}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[var(--bg-input)] transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />}
                    <span className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest truncate">{country}</span>
                  </div>
                  <div className="flex items-center gap-4 text-right shrink-0">
                    <span className="text-2xs font-black text-[var(--text-secondary)]">{countryUsers.toLocaleString('nb-NO')}</span>
                    <span className="text-xs font-black text-[var(--text-primary)]">{countryViews.toLocaleString('nb-NO')}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[var(--border-secondary)]">
                          <th className="text-left text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] py-3 px-4">Fylke</th>
                          <th className="text-right text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] py-3 px-4">{showPaidOnly ? 'Betalt' : 'Brukere'}</th>
                          <th className="text-right text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] py-3 px-4">Visninger</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countryRows.map(row => (
                          <tr key={row.county} className="border-b border-[var(--border-primary)]">
                            <td className="py-2.5 px-4 text-xs font-bold text-[var(--text-secondary)]">{row.county}</td>
                            <td className="py-2.5 px-4 text-xs font-black text-[var(--text-primary)] text-right">{row.users || '–'}</td>
                            <td className="py-2.5 px-4 text-xs font-black text-[var(--text-primary)] text-right">{row.views ? row.views.toLocaleString('nb-NO') : '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminRegionDashboard;
